<script setup>
import { computed, ref, onMounted, nextTick, watch, useAttrs } from 'vue';
import { useResizeObserver } from '@vueuse/core';

const props = defineProps({
  initialActiveTab: {
    type: Number,
    default: 0,
  },
  variant: {
    type: String,
    default: 'default',
  },
  tabs: {
    type: Array,
    required: true,
    validator: value => {
      return value.every(
        tab =>
          typeof tab.label === 'string' &&
          (tab.count ? typeof tab.count === 'number' : true)
      );
    },
  },
});

const emit = defineEmits(['tabChanged']);
const attrs = useAttrs();

defineOptions({
  inheritAttrs: false,
});

const activeTab = computed(() => props.initialActiveTab);
const isPillVariant = computed(() => props.variant === 'pills');

const scrollContainerRef = ref(null);
const tabRefs = ref([]);
const indicatorStyle = ref({});
const enableTransition = ref(false);

const activeElement = computed(() => tabRefs.value[activeTab.value]);

const updateIndicator = () => {
  nextTick(() => {
    if (!activeElement.value || !scrollContainerRef.value) return;

    indicatorStyle.value = {
      left: `${activeElement.value.offsetLeft}px`,
      width: `${activeElement.value.offsetWidth}px`,
    };

    if (isPillVariant.value) {
      const container = scrollContainerRef.value;
      const targetLeft =
        activeElement.value.offsetLeft -
        container.clientWidth / 2 +
        activeElement.value.offsetWidth / 2;

      const maxScrollLeft = container.scrollWidth - container.clientWidth;
      const safeScrollLeft = Math.max(0, Math.min(targetLeft, maxScrollLeft));

      container.scrollTo({
        left: safeScrollLeft,
        behavior: 'auto',
      });
      return;
    }

    activeElement.value.scrollIntoView({
      behavior:
        enableTransition.value && !isPillVariant.value ? 'smooth' : 'auto',
      block: 'nearest',
      inline: 'nearest',
    });
  });
};

useResizeObserver(activeElement, updateIndicator);

// Watch for prop/tabs changes to update indicator position
watch([() => props.initialActiveTab, () => props.tabs], updateIndicator, {
  immediate: true,
});

onMounted(() => {
  nextTick(() => {
    enableTransition.value = true;
  });
});

const selectTab = index => {
  emit('tabChanged', props.tabs[index]);
};

const showDivider = index => {
  if (isPillVariant.value) return false;
  return (
    // Show dividers after the active tab, but not after the last tab
    (index > activeTab.value && index < props.tabs.length - 1) ||
    // Show dividers before the active tab, but not immediately before it and not before the first tab
    (index < activeTab.value - 1 && index > -1)
  );
};

const containerClass = computed(() =>
  isPillVariant.value
    ? 'gap-1.5 bg-transparent h-9 p-0'
    : 'rounded-lg bg-n-alpha-1 dark:bg-n-solid-1 h-8'
);

const indicatorClass = computed(() =>
  isPillVariant.value
    ? 'rounded-full bg-n-slate-3 dark:bg-n-slate-3 shadow-none outline-0 h-9'
    : 'rounded-lg bg-n-solid-active shadow-sm outline-1 outline outline-n-container h-8'
);

const tabClass = computed(() =>
  isPillVariant.value
    ? 'px-5 truncate py-2 text-sm border-0 outline-0 rounded-full'
    : 'px-4 truncate py-1.5 text-sm border-0 outline-1 outline-transparent rounded-lg transition-all duration-200 ease-out hover:text-n-brand active:scale-[1.02]'
);
</script>

<template>
  <div ref="scrollContainerRef" class="w-full overflow-x-auto no-scrollbar">
    <div
      v-bind="attrs"
      class="relative flex items-center w-max"
      :class="[
        containerClass,
        {
          'transition-all duration-200 ease-out has-[button:active]:scale-[1.01]':
            !isPillVariant,
        },
      ]"
    >
      <div
        class="absolute pointer-events-none inset-y-0"
        :class="[
          indicatorClass,
          {
            'transition-all duration-300 ease-out':
              enableTransition && !isPillVariant,
          },
        ]"
        :style="indicatorStyle"
      />

      <template v-for="(tab, index) in tabs" :key="index">
        <button
          :ref="el => (tabRefs[index] = el)"
          class="relative z-10"
          :class="[
            tabClass,
            isPillVariant
              ? activeTab === index
                ? '!text-black dark:!text-black scale-100 font-normal'
                : 'text-black dark:text-black bg-transparent hover:bg-transparent scale-100 font-normal'
              : activeTab === index
                ? 'text-n-brand scale-100'
                : 'text-n-slate-10 scale-[0.98]',
          ]"
          @click="selectTab(index)"
        >
          {{ tab.label }} {{ tab.count ? `(${tab.count})` : '' }}
        </button>
        <div
          v-if="index < tabs.length - 1"
          class="w-px h-3.5 rounded my-auto transition-colors duration-300 ease-in-out"
          :class="
            showDivider(index)
              ? 'bg-n-strong'
              : 'bg-transparent dark:bg-transparent'
          "
        />
      </template>
    </div>
  </div>
</template>
